import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createApiClient } from '../api/client';
import { curatedErrorMessage } from '../lib/curatedError';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from './WorkspaceProvider';
import type { BootstrapFounderRequest, CreateWorkspaceResponse } from '../api/types';
import { useFocusTrap } from '../lib/useFocusTrap';
import { hasVisibleContent } from '@steward-app/steward-types';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';
import { slugify, clampSlug, slugTooShort, SLUG_MAX } from '../lib/workspaceSlug';
import { SlugShortNote } from './SlugShortNote';
import { cls as formCls } from '../components/formStyles';
import { TermsAcceptanceLine } from '../terms/TermsAcceptanceLine';

const ICON_STROKE = 1.75;

const FOUNDER_FAILED_MESSAGE = 'Could not create your organization.';
export const FOUNDER_ERROR_COPY: Readonly<Record<string, string>> = {
  '403:NOT_ELIGIBLE': 'We couldn’t find an active subscription for your account. Contact support if you believe this is an error.',
  '409': 'That organization or workspace name is already taken. Try another.',
};

/**
 * Founder front door (O1). Creates the caller's organization + first workspace +
 * admin membership in one atomic call via the façade (`POST /onboarding/bootstrap`
 * → appcore, which consumes the caller's signup-eligibility). Distinct from
 * {@link NewWorkspaceDialog}, which adds a workspace under an *existing* tenant and
 * needs a tenantId the founder lacks.
 *
 * NOT only for member-less users. The caller may already belong to someone else's
 * workspace and still hold an unconsumed grant for a tenant of their own (#430),
 * which is why this is reachable from the workspace switcher as well as the picker.
 * Bootstrapping from inside an existing session is safe: it refreshes `/me` and then
 * switches into the newly created workspace, leaving the prior membership untouched.
 */
export function FounderBootstrapDialog({ onClose }: { onClose: () => void }) {
  const token = useAccessToken();
  const { refresh, switchWorkspace, acceptTerms } = useWorkspace();

  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [orgSlugTouched, setOrgSlugTouched] = useState(false);
  const [wsName, setWsName] = useState('');
  const [wsSlug, setWsSlug] = useState('');
  const [wsSlugTouched, setWsSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effectiveOrgSlug = orgSlugTouched ? clampSlug(orgSlug) : slugify(orgName);
  // The first workspace defaults its name + slug from the org so a founder who just
  // wants one space can submit after a single field; both stay independently editable.
  const effectiveWsName = wsName.trim() ? wsName : orgName;
  const effectiveWsSlug = wsSlugTouched ? clampSlug(wsSlug) : slugify(effectiveWsName);
  // A name of only invisible characters is held back like an empty one, with the reason shown
  // in the slot this form uses for its other errors (crossmarc/steward-app#2041).
  const invisibleName = [orgName, effectiveWsName].some(n => n.trim().length > 0 && !hasVisibleContent(n.trim()));

  // A derived or typed web address under 2 characters is held back before the request, in the
  // same slot (crossmarc/steward-app#2050). Only judged once there is a name or an edit to judge.
  const orgShort = slugTooShort(effectiveOrgSlug, orgName.trim().length > 0 || orgSlugTouched);
  const wsShort = slugTooShort(effectiveWsSlug, effectiveWsName.trim().length > 0 || wsSlugTouched);
  const slugShort = orgShort || wsShort;
  // Each short field gets its own message, shown once its name was left, the field was edited, or a
  // submit was attempted (no flash at the first keystroke). The button stays enabled so a click can explain.
  const [orgNameBlurred, setOrgNameBlurred] = useState(false);
  const [wsNameBlurred, setWsNameBlurred] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const orgShortShown = orgShort && !invisibleName && (orgNameBlurred || orgSlugTouched || attempted);
  const wsShortShown = wsShort && !invisibleName
    && (wsNameBlurred || (!wsName.trim() && orgNameBlurred) || wsSlugTouched || attempted);

  // A click or Enter on an under-length address sends nothing, shows each short field's note and
  // moves focus to the first short one. The effect runs after that render, so the notes are in place.
  const [focusReq, setFocusReq] = useState<{ id: string } | null>(null);
  useEffect(() => {
    if (focusReq) document.getElementById(focusReq.id)?.focus();
  }, [focusReq]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || submitting || invisibleName) return;
    if (slugShort) { setAttempted(true); setFocusReq({ id: orgShort ? 'bootstrap-org-slug' : 'bootstrap-ws-slug' }); return; }
    setSubmitting(true);
    setErr(null);
    try {
      const payload: BootstrapFounderRequest = {
        tenantName: orgName.trim(),
        tenantSlug: effectiveOrgSlug,
        workspaceName: effectiveWsName.trim(),
        workspaceSlug: effectiveWsSlug,
      };
      // The click, with TermsAcceptanceLine beneath the button, is the acceptance (steward-app#2630).
      await acceptTerms();
      const created = await createApiClient(token).post<CreateWorkspaceResponse>('/onboarding/bootstrap', payload);
      await refresh();
      switchWorkspace(created.workspaceId);
      onClose();
    } catch (e2) {
      // Default-deny (crossmarc/steward-app#2047): appcore's 403 `NOT_ELIGIBLE` (no signup
      // eligibility, read in appcore's `bootstrapFounder`) and a 409 slug collision get SPA copy.
      // The 403 is keyed by code, not bare status, so a step-up 403 falls to the fallback.
      const msg = curatedErrorMessage(e2, FOUNDER_ERROR_COPY, FOUNDER_FAILED_MESSAGE, 'Founder bootstrap');
      setErr(msg);
      setSubmitting(false);
    }
  }

  const canSubmit =
    !!token && !submitting && !invisibleName &&
    !!orgName.trim() && !!effectiveWsName.trim();

  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // Portalled to <body> so the overlay escapes any sticky/stacking ancestor.
  return createPortal((
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Create your organization" ref={trapRef}>
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} aria-hidden />
      <form
        onSubmit={submit}
        className="relative w-full max-w-[420px] rounded-token border border-line bg-surface p-6 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate transition-colors duration-150 hover:text-navy"
        >
          <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </button>

        <div className="font-display text-[16px] font-semibold text-navy mb-1">Create your organization</div>
        <div className="text-[12px] text-slate font-sans mb-4">Two names and you are in. Both can be changed anytime.</div>

        {/* ── Organization ── */}
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate mb-2">Organization</div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="bootstrap-org-name">Name</label>
        <input
          id="bootstrap-org-name"
          className={`${formCls.input} mb-3`}
          value={orgName}
          onChange={e => setOrgName(e.target.value)}
          onBlur={() => setOrgNameBlurred(true)}
          maxLength={200}
          placeholder="e.g. Anderson Family Office"
          autoFocus
          required
        />
        <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="bootstrap-org-slug">Web address</label>
        <input
          id="bootstrap-org-slug"
          className={`${formCls.input} mb-1`}
          value={effectiveOrgSlug}
          onChange={e => { setOrgSlugTouched(true); setOrgSlug(e.target.value); }}
          placeholder="anderson-family-office"
          pattern={'[a-z0-9\\-]+'}
          maxLength={SLUG_MAX}
          aria-invalid={orgShortShown || undefined}
          aria-describedby={orgShortShown ? 'bootstrap-org-slug-short' : undefined}
        />
        {orgShortShown && <SlugShortNote id="bootstrap-org-slug-short" />}
        <div className="text-[11px] text-mid font-sans mb-4">Created from the name. Lowercase letters, numbers, and hyphens.</div>

        {/* ── First workspace ── */}
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate mb-2">First workspace</div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="bootstrap-ws-name">Name</label>
        <input
          id="bootstrap-ws-name"
          className={`${formCls.input} mb-3`}
          value={effectiveWsName}
          onChange={e => setWsName(e.target.value)}
          onBlur={() => setWsNameBlurred(true)}
          maxLength={200}
          placeholder="e.g. Anderson Family"
          required
        />
        <label className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans mb-1" htmlFor="bootstrap-ws-slug">Web address</label>
        <input
          id="bootstrap-ws-slug"
          className={`${formCls.input} mb-1`}
          value={effectiveWsSlug}
          onChange={e => { setWsSlugTouched(true); setWsSlug(e.target.value); }}
          placeholder="anderson-family"
          pattern={'[a-z0-9\\-]+'}
          maxLength={SLUG_MAX}
          aria-invalid={wsShortShown || undefined}
          aria-describedby={wsShortShown ? 'bootstrap-ws-slug-short' : undefined}
        />
        {wsShortShown && <SlugShortNote id="bootstrap-ws-slug-short" />}
        <div className="text-[11px] text-mid font-sans mb-4">Created from the name. Lowercase letters, numbers, and hyphens.</div>

        {(invisibleName || err) && <div className="mb-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{invisibleName ? INVISIBLE_NAME_MESSAGE : err}</div>}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Preparing your workspace…' : 'Create organization'}
        </button>
        <TermsAcceptanceLine className="m-0 mt-3 text-center" />
      </form>
    </div>
  ), document.body);
}
